// Record access (ניהול הרשאות רשומות) — client side of
// docs/design/DESIGN-2026-10-14-record-access-permissions.md.
//
// The server decides; this only reflects it on the record screen. Rules apply
// only while the flag `record_access.enforce` is on (the effective endpoint
// says so in `enforced`), and a system admin is never restricted.

import { useEffect, useState } from 'react';
import config from '../config/config';
import { authHeaders, getSessionToken } from './session';

const API_BASE = config.api.baseUrl;

// Sent by the record screen's own save (desktop + mobile) — and only there — so
// the server applies "read-only on the record screen" to it and not to the
// dedicated components that write the same fields.
export const RECORD_SCREEN_EDIT_HEADERS = { 'X-Afikim-Edit-Source': 'record_screen' };

export const NO_RESTRICTIONS = Object.freeze({
  enforced: false, admin: false, can_view: true, can_edit_record: true, can_delete: true, readonly_fields: [],
});

/** The caller's effective access to an object; NO_RESTRICTIONS on any failure. */
export async function fetchRecordAccess(objectName) {
  if (!objectName) return NO_RESTRICTIONS;
  try {
    const res = await fetch(`${API_BASE}/api/record-access/effective/${encodeURIComponent(objectName)}`,
      { headers: authHeaders() });
    if (!res.ok) return NO_RESTRICTIONS;
    const d = await res.json();
    return { ...NO_RESTRICTIONS, ...d, readonly_fields: Array.isArray(d.readonly_fields) ? d.readonly_fields : [] };
  } catch {
    return NO_RESTRICTIONS;
  }
}

/** Fields to show read-only on the record screen (empty unless enforced). */
export function lockedFieldSet(access) {
  if (!access || !access.enforced || access.admin) return new Set();
  return new Set(access.readonly_fields || []);
}

/** May this user open "עריכה" on the record screen? (true unless enforced and denied) */
export function canEditOnScreen(access) {
  return !access || !access.enforced || !!access.admin || access.can_edit_record !== false;
}

/** May this user delete records of the object? (true unless enforced and denied) */
export function canDeleteRecords(access) {
  return !access || !access.enforced || !!access.admin || access.can_delete !== false;
}

/** A readable message for the server's record-access refusals, or null. */
export function recordAccessErrorMessage(detail, labelOf = (f) => f) {
  if (!detail || typeof detail !== 'object') return null;
  if (detail.code === 'record_edit_denied' || detail.code === 'delete_denied') {
    return detail.message || 'אין הרשאה';
  }
  if (detail.code === 'field_readonly' || detail.code === 'field_edit_blocked') {
    const fields = (detail.fields || []).map(labelOf).join(', ');
    return `${detail.message || 'אין הרשאה לערוך'}${fields ? `: ${fields}` : ''}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Objects hidden from this user (PR 5, owner decision D2: UI hiding only).
// One request per session token; every surface (launcher, nav, search, related
// lists, list / record routes) reads the same Set of lower-cased api names.
// Empty unless record_access.enforce is on; empty on any failure.
// ---------------------------------------------------------------------------
const EMPTY_SET = new Set();
let _hidden = { token: undefined, promise: null, value: EMPTY_SET };

export function loadHiddenObjects() {
  const token = getSessionToken() || '';
  if (_hidden.token === token && _hidden.promise) return _hidden.promise;
  const entry = { token, promise: null, value: EMPTY_SET };
  entry.promise = fetch(`${API_BASE}/api/record-access/hidden-objects`, { headers: authHeaders() })
    .then(r => (r.ok ? r.json() : null))
    .then(d => {
      const set = d && d.enforced ? new Set((d.hidden || []).map(x => String(x).toLowerCase())) : EMPTY_SET;
      entry.value = set;
      return set;
    })
    .catch(() => EMPTY_SET);
  _hidden = entry;
  return entry.promise;
}

/** For tests / logout: forget the cached list. */
export function resetHiddenObjects() {
  _hidden = { token: undefined, promise: null, value: EMPTY_SET };
}

/** Set of lower-cased api names to hide; re-reads when `userKey` changes. */
export function useHiddenObjects(userKey) {
  const [hidden, setHidden] = useState(_hidden.value);
  useEffect(() => {
    let cancelled = false;
    loadHiddenObjects().then(s => { if (!cancelled) setHidden(s); });
    return () => { cancelled = true; };
  }, [userKey]);
  return hidden;
}

export const isObjectHidden = (hidden, objectName) =>
  !!objectName && !!hidden && hidden.has(String(objectName).toLowerCase());
